import { createCxApiClient } from '@mangovoice/api-client'

import type { components, paths } from './generated/schema'

export type { components, paths }
export type AgentStatus = components['schemas']['AgentStatus']
export type AgentStatusCreate = components['schemas']['AgentStatusCreate']
export type AgentStatusPatch = components['schemas']['AgentStatusPatch']
export type UserAgent = components['schemas']['UserAgent']
export type UserAgentPatch = components['schemas']['UserAgentPatch']
export type EventLog = components['schemas']['EventLog']
export type PaginatedAgentStatusList =
  components['schemas']['PaginatedAgentStatusList']
export type PaginatedUserAgentList =
  components['schemas']['PaginatedUserAgentList']
export type PaginatedEventLogList =
  components['schemas']['PaginatedEventLogList']

export const CCUA_GATEWAY_PREFIX = '/call-center-user-agents/v1' as const

const PBX_AGENT_STATUSES =
  '/call-center-user-agents/v1/pbxes/{pbx_uuid}/agent-statuses' as const
const PBX_AGENT_STATUS =
  '/call-center-user-agents/v1/pbxes/{pbx_uuid}/agent-statuses/{uuid}' as const
const PBX_USER_AGENTS =
  '/call-center-user-agents/v1/pbxes/{pbx_uuid}/user-agents' as const
const PBX_USER_AGENT =
  '/call-center-user-agents/v1/pbxes/{pbx_uuid}/user-agents/{extension_uuid}' as const
const PBX_EVENT_LOGS =
  '/call-center-user-agents/v1/pbxes/{pbx_uuid}/event-logs' as const

type CxClient = ReturnType<typeof createCxApiClient<paths>>

function requireMethod<T>(
  method: T | undefined,
  verb: string,
  path: string,
): T {
  if (!method) {
    throw new Error(`${verb} ${path} is missing from the generated client`)
  }
  return method
}

export interface ListQuery {
  limit?: number
  offset?: number
}

export interface ListUserAgentsQuery extends ListQuery {
  since?: string
  until?: string
  agent_status_id?: string
  availability?: 'Online' | 'Away'
}

export interface ListEventLogsQuery extends ListQuery {
  since?: string
  until?: string
  logged_in?: boolean
  user_uuid?: string
  initiated_by_user_uuid?: string
}

/** OpenAPI `Limit` maximum on the CCUA gateway list endpoints. */
export const GATEWAY_MAX_PAGE_SIZE = 100

type GatewayListPage = {
  items?: unknown[]
  total?: number
  limit?: number
  offset?: number
}

function asGatewayListPage(data: unknown): GatewayListPage | null {
  if (!data || typeof data !== 'object' || !('items' in data)) {
    return null
  }
  return data as GatewayListPage
}

/**
 * Walks gateway list pages until every row is collected. Single-shot
 * `limit: 999` exceeds the gateway max of 100 and silently truncates.
 */
export async function collectAllGatewayItems<T>(
  fetchPage: (query: {
    limit: number
    offset: number
  }) => Promise<{ ok: boolean; data?: unknown }>,
): Promise<{ ok: true; items: T[] } | { ok: false }> {
  const items: T[] = []
  let offset = 0
  for (let page = 0; page < 50; page++) {
    const { ok, data } = await fetchPage({
      limit: GATEWAY_MAX_PAGE_SIZE,
      offset,
    })
    const listPage = asGatewayListPage(data)
    if (!ok || !listPage) {
      return { ok: false }
    }
    const pageItems = (listPage.items ?? []) as T[]
    items.push(...pageItems)
    const pageLimit =
      listPage.limit && listPage.limit > 0
        ? listPage.limit
        : GATEWAY_MAX_PAGE_SIZE
    if (
      items.length >= (listPage.total ?? items.length) ||
      pageItems.length < pageLimit
    ) {
      return { ok: true, items }
    }
    const nextOffset = (listPage.offset ?? offset) + pageLimit
    if (nextOffset <= offset) {
      return { ok: true, items }
    }
    offset = nextOffset
  }
  return { ok: true, items }
}

/**
 * Django-shaped page so existing infinite-query helpers can parse `next`.
 *
 * Prefer the response's `limit`/`offset` when the gateway echoes them; fall
 * back to the values we requested so a missing or zero `limit` cannot NaN the
 * next-page offset.
 */
export function toDjangoPage<T>(
  page: {
    items: T[]
    total: number
    limit?: number | null
    offset?: number | null
  },
  requested?: { limit: number; offset: number },
): {
  results: T[]
  count: number
  next: string | null
  previous: string | null
} {
  const limit =
    page.limit && page.limit > 0 ? page.limit : (requested?.limit ?? 0)
  const offset = page.offset ?? requested?.offset ?? 0
  const nextOffset = limit > 0 ? offset + limit : offset
  return {
    results: page.items,
    count: page.total,
    next:
      limit > 0 && nextOffset < page.total
        ? `?limit=${limit}&offset=${nextOffset}`
        : null,
    previous:
      offset > 0 && limit > 0
        ? `?limit=${limit}&offset=${Math.max(0, offset - limit)}`
        : null,
  }
}

export function createCallCenterUserAgentsClient(
  transport: Parameters<typeof createCxApiClient>[0],
) {
  const client: CxClient = createCxApiClient<paths>(transport)

  return {
    listAgentStatuses(pbxUuid: string, query?: ListQuery) {
      const { get } = client(PBX_AGENT_STATUSES)({ pbx_uuid: pbxUuid })
      return requireMethod(get, 'GET', PBX_AGENT_STATUSES)(query)
    },

    createAgentStatus(pbxUuid: string, body: AgentStatusCreate) {
      const { post } = client(PBX_AGENT_STATUSES)({ pbx_uuid: pbxUuid })
      return requireMethod(post, 'POST', PBX_AGENT_STATUSES)(body)
    },

    patchAgentStatus(pbxUuid: string, uuid: string, body: AgentStatusPatch) {
      const { patch } = client(PBX_AGENT_STATUS)({
        pbx_uuid: pbxUuid,
        uuid,
      })
      return requireMethod(patch, 'PATCH', PBX_AGENT_STATUS)(body)
    },

    deleteAgentStatus(pbxUuid: string, uuid: string) {
      const { delete: del } = client(PBX_AGENT_STATUS)({
        pbx_uuid: pbxUuid,
        uuid,
      })
      return requireMethod(del, 'DELETE', PBX_AGENT_STATUS)()
    },

    listUserAgents(pbxUuid: string, query?: ListUserAgentsQuery) {
      const { get } = client(PBX_USER_AGENTS)({ pbx_uuid: pbxUuid })
      return requireMethod(get, 'GET', PBX_USER_AGENTS)(query)
    },

    getUserAgent(pbxUuid: string, extensionUuid: string) {
      const { get } = client(PBX_USER_AGENT)({
        pbx_uuid: pbxUuid,
        extension_uuid: extensionUuid,
      })
      return requireMethod(get, 'GET', PBX_USER_AGENT)()
    },

    patchUserAgent(
      pbxUuid: string,
      extensionUuid: string,
      body: UserAgentPatch,
    ) {
      const { patch } = client(PBX_USER_AGENT)({
        pbx_uuid: pbxUuid,
        extension_uuid: extensionUuid,
      })
      return requireMethod(patch, 'PATCH', PBX_USER_AGENT)(body)
    },

    listEventLogs(pbxUuid: string, query?: ListEventLogsQuery) {
      const { get } = client(PBX_EVENT_LOGS)({ pbx_uuid: pbxUuid })
      return requireMethod(get, 'GET', PBX_EVENT_LOGS)(query)
    },
  }
}

export type CallCenterUserAgentsClient = ReturnType<
  typeof createCallCenterUserAgentsClient
>

export {
  isAssignedAgentStatus,
  mapAvailability,
  mapCustomStatus,
  mapEventLog,
  mapGatewayAgentStatus,
  mapGatewayEventLog,
  mapGatewayUserAgent,
  mapUserAgent,
  toOpaqueId,
  toRfc3339DayEnd,
  toRfc3339DayStart,
} from './mappers'
export type {
  UnifiedAvailability,
  UnifiedCustomStatus,
  UnifiedEventLog,
  UnifiedUserAgent,
} from './mappers'
